import {
  Activity,
  ChevronDown,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Settings,
  ShieldCheck,
  Sun,
  Ticket,
  User,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type PropsWithChildren } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Link, NavLink, useLocation } from '../lib/router';
import { useTheme } from '../lib/theme';

type ApiEnvironments = {
  app: { environment: 'localhost' | 'development' | 'production' };
  quiApi: { label: string };
  partnerApi: { label: string };
};

const appEnvironmentLabels: Record<string, string> = {
  localhost: 'Localhost',
  development: 'Desenvolvimento',
};

const roleLabels: Record<string, string> = {
  SUPER_ADMIN: 'Super administrador',
  OPERATIONS: 'Operações',
  COMPLIANCE: 'Compliance',
  SUPPORT: 'Suporte',
  AUDITOR: 'Auditoria',
};

export function AppShell({ children }: PropsWithChildren) {
  const { admin, logout } = useAuth();
  const { theme, setPreference } = useTheme();
  const { pathname, search } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileTriggerRef = useRef<HTMLButtonElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [environments, setEnvironments] = useState<ApiEnvironments | null>(null);
  const canAudit = ['SUPER_ADMIN', 'AUDITOR', 'COMPLIANCE'].includes(
    admin?.role ?? '',
  );
  const canAdmins = admin?.role === 'SUPER_ADMIN';
  const canPagaqui = ['SUPER_ADMIN', 'OPERATIONS'].includes(admin?.role ?? '');
  const isReviewQueue =
    pathname === '/customers' &&
    new URLSearchParams(search).get('queue') === 'kyc';
  const isCustomersSection =
    (pathname === '/customers' || pathname.startsWith('/customers/')) &&
    !isReviewQueue;

  useEffect(() => {
    api<ApiEnvironments>('/system/environment')
      .then(setEnvironments)
      .catch(() => setEnvironments(null));
  }, []);

  useEffect(() => {
    if (!profileMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (!profileMenuRef.current?.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setProfileMenuOpen(false);
        profileTriggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [profileMenuOpen]);

  useEffect(() => {
    setProfileMenuOpen(false);
  }, [pathname, search, mobileOpen]);

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileOpen ? 'sidebar--open' : ''}`}>
        <div className="brand">
          <img className="brand__logo" src="/brand/pagaqui.svg" alt="PAGAQUI" />
          <div>
            <strong>Operations</strong>
            <span>Backoffice</span>
          </div>
          <button
            className="icon-button sidebar__close"
            onClick={() => setMobileOpen(false)}
            aria-label="Fechar menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="nav" aria-label="Navegação principal">
          <span className="nav__label">Operações</span>
          <NavLink to="/" end onClick={() => setMobileOpen(false)}>
            <LayoutDashboard size={19} />
            Visão geral
          </NavLink>
          <NavLink
            to="/customers"
            active={isCustomersSection}
            onClick={() => setMobileOpen(false)}
          >
            <Users size={19} />
            Clientes
          </NavLink>
          <NavLink
            to="/customers?queue=kyc"
            active={isReviewQueue}
            onClick={() => setMobileOpen(false)}
          >
            <ClipboardCheck size={19} />
            Revisão KYC
          </NavLink>
          {canPagaqui && (
            <>
              <span className="nav__label nav__label--spaced">Pagaqui</span>
              <NavLink to="/pagaqui/settings" onClick={() => setMobileOpen(false)}>
                <Settings size={19} />
                Configurações
              </NavLink>
              <NavLink to="/pagaqui/vouchers" onClick={() => setMobileOpen(false)}>
                <Ticket size={19} />
                Vouchers
              </NavLink>
            </>
          )}
          {canAudit && (
            <>
              <span className="nav__label nav__label--spaced">Controlo</span>
              <NavLink to="/audit" onClick={() => setMobileOpen(false)}>
                <Activity size={19} />
                Auditoria
              </NavLink>
            </>
          )}
          {canAdmins && (
            <NavLink to="/admins" onClick={() => setMobileOpen(false)}>
              <ShieldCheck size={19} />
              Administradores
            </NavLink>
          )}
        </nav>

        <div className="sidebar-account" ref={profileMenuRef}>
          <button
            ref={profileTriggerRef}
            type="button"
            className="sidebar-account__trigger"
            onClick={() => setProfileMenuOpen(value => !value)}
            aria-expanded={profileMenuOpen}
            aria-controls="sidebar-account-submenu"
          >
            <span className="avatar">{admin?.fullName.slice(0, 2).toUpperCase()}</span>
            <span className="sidebar-account__identity"><strong>{admin?.fullName}</strong><small>{roleLabels[admin?.role ?? '']}</small></span>
            <ChevronDown size={17} className={profileMenuOpen ? 'sidebar-account__chevron--open' : ''} />
          </button>
          {profileMenuOpen && (
            <nav id="sidebar-account-submenu" className="sidebar-account__submenu" aria-label="Conta do utilizador">
              <Link to="/account?tab=profile" onClick={() => { setProfileMenuOpen(false); setMobileOpen(false); }}><User size={17} />Perfil</Link>
              <Link to="/account?tab=security" onClick={() => { setProfileMenuOpen(false); setMobileOpen(false); }}><ShieldCheck size={17} />Segurança</Link>
              <button type="button" onClick={logout}><LogOut size={17} />Terminar sessão</button>
            </nav>
          )}
        </div>
      </aside>

      {mobileOpen && (
        <button
          className="sidebar-overlay"
          aria-label="Fechar menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <main className="main">
        <header className="topbar">
          <button
            className="icon-button topbar__menu"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu size={22} />
          </button>
          {environments && appEnvironmentLabels[environments.app.environment] && (
            <span className="env-badge" title="Ambiente do backoffice">
              {appEnvironmentLabels[environments.app.environment]}
            </span>
          )}
          <div className="topbar__environment" title="APIs ligadas">
            <span className="pulse" />
            {environments ? (
              <span>
                QUI API · {environments.quiApi.label}
                {' — '}
                Partner API · {environments.partnerApi.label}
              </span>
            ) : (
              <span>Qui. Backoffice</span>
            )}
          </div>
          <button
            className="icon-button theme-toggle"
            onClick={() => setPreference(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
            aria-label="Alternar tema"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </header>
        <div className="main__content">
          {children}
        </div>
      </main>
    </div>
  );
}
